import { createFileRoute, Link } from "@tanstack/react-router";
import { RouteError, RouteNotFound } from "@/components/route-error";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  getDueReminders,
  completeReminder,
  getHomeDigest,
  recordHookFeedback,
  undoHookFeedback,
  actOnSuggestion,
  wakeSuggestion,
  listSnoozedSuggestions,
  getScanStatus,
  getProfile,
  addMemory,
} from "@/lib/relationships.functions";
import { dismissSignal } from "@/lib/signals.functions";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { toast } from "sonner";
import {
  Clock, Star, Sparkles, Bell, Check, X,
  ChevronDown, Inbox, Undo2,
} from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { openAddDrawer } from "@/lib/add-drawer-store";
import { ConnectionStatus } from "@/components/connection-status";
import { CalendarConnectCard } from "@/components/calendar-connect-card";
import { PushOptInRow } from "@/components/push-optin-row";
import { useGoogleConnections } from "@/components/google-connection-row";
import { AccountChip } from "@/components/account-chip";
import { Avatar } from "@/components/avatar";
import { listPendingHooks, listPendingFreshLinkHooks, updateHookStatus, getCalendarStatus, saveHookMemory, syncGoogleCalendar, listPostMeetingRecaps } from "@/lib/calendar.functions";
import { PostMeetingRecapCard } from "@/components/post-meeting-recap-card";
import { TooltipProvider } from "@/components/ui/tooltip";
import { TodayFeedSkeleton } from "@/components/loading-skeletons";
import { InlineMemoryComposer } from "@/components/inline-memory-composer";

import { HookDeck } from "@/components/hook-deck";
import { useContactSheet } from "@/components/contact-sheet-provider";

import { ComingUpCard } from "@/components/coming-up-card";
import { TodayFeed } from "@/components/today-feed";
import { getOpportunityFeed, actOnOpportunity } from "@/lib/opportunities.functions";
import { mergeOpportunities } from "@/lib/merge-opportunities";
import { countUrgent } from "@/lib/urgency";
import { track } from "@/lib/track";
import { FeedbackSheet } from "@/components/feedback-sheet";
import { filterCooledDown } from "@/lib/today-rank";
import { useAuth } from "@/hooks/use-auth";
import { clampDailyTouches, planOf } from "@/lib/plans";
import { RailsTutorial, type RailsStep } from "@/components/rails-tutorial";
import { restoreSignal } from "@/lib/signals.functions";
import { LockedMoments } from "@/components/locked-moments";
import { TripStrip, useActiveTrips } from "@/components/travel/trip-strip";
import { EnginePendingStrip } from "@/components/engine-pending-strip";
import { ConnectionBrokenBanner } from "@/components/connection-broken-banner";
import { CoachCard } from "@/components/coach-card";
import { WelcomeTour } from "@/components/welcome-tour";
import { listContactChanges } from "@/lib/contact-changes.functions";
import { ProfileChangeCard, type ProfileChange } from "@/components/profile-change-card";

export const Route = createFileRoute("/_authenticated/today")({
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound, component: Today });

function formatRelativeShort(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return "just now";
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function DesktopTodayHeader({
  date,
  headline,
  subheadline,
  connectionStatus,
  scanActive,
}: {
  date: string;
  headline: ReactNode;
  subheadline?: string;
  connectionStatus: ReactNode;
  scanActive: boolean;
}) {
  return (
    <header className="hidden pt-1 md:block">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4">
        <p className="flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground">
          {scanActive && <span className="neon-pulse-dot" />}
          {date}
        </p>
        <div className="shrink-0">
          <AccountChip />
        </div>
      </div>
      <div className="mt-2 grid grid-cols-[minmax(0,1fr)_auto] items-end gap-4">
        <div className="min-w-0">
          <h1 className="text-[26px] font-semibold leading-tight tracking-tight sm:text-3xl md:text-4xl">
            {headline}
          </h1>
          {subheadline && (
            <p className="mt-1 text-[14px] font-medium text-[var(--urgency-red)]">
              {subheadline} · start at the top
            </p>
          )}
        </div>
        <div className="shrink-0 pb-1">{connectionStatus}</div>
      </div>
    </header>
  );
}

function Today() {
  const { user } = useAuth();
  const authed = !!user;
  const fetchReminders = useServerFn(getDueReminders);
  const fetchDigest = useServerFn(getHomeDigest);
  const completeRem = useServerFn(completeReminder);
  const feedback = useServerFn(recordHookFeedback);
  const remember = useServerFn(addMemory);
  const dismissSig = useServerFn(dismissSignal);
  const fetchPendingHooks = useServerFn(listPendingHooks);
  const fetchFreshLinkHooks = useServerFn(listPendingFreshLinkHooks);
  const updHook = useServerFn(updateHookStatus);
  const undoSignal = useServerFn(restoreSignal);
  const actSug = useServerFn(actOnSuggestion);
  const wakeSug = useServerFn(wakeSuggestion);
  const fetchSnoozed = useServerFn(listSnoozedSuggestions);
  const fetchCalStatus = useServerFn(getCalendarStatus);
  const fetchScanStatus = useServerFn(getScanStatus);
  const fetchOpportunities = useServerFn(getOpportunityFeed);
  const actOpp = useServerFn(actOnOpportunity);
  const fetchProfile = useServerFn(getProfile);
  const qc = useQueryClient();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // One "today_viewed" per calendar day powers the week-2 return KPI in /admin.
  // localStorage, not sessionStorage: a returning user opening a fresh tab is
  // the same day-active user, and counting them twice inflates retention.
  useEffect(() => {
    if (!authed) return;
    const key = `pyng:today-viewed:${new Date().toDateString()}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    } catch {
      /* private mode — track anyway */
    }
    track("today_viewed", { source: "today" });
  }, [authed]);
  const { data: profile, isPending: profilePending } = useQuery({
    queryKey: ["profile"],
    queryFn: () => fetchProfile(),
    staleTime: 5 * 60 * 1000,
    enabled: authed,
  });
  const { data: digest, isPending: digestPending } = useQuery({
    queryKey: ["home-digest"],
    queryFn: () => fetchDigest(),
    staleTime: 30 * 1000,
    enabled: authed,
  });
  const { data: reminders } = useQuery({ queryKey: ["reminders-due"], queryFn: () => fetchReminders(), enabled: authed });
  const { data: oppData, isPending: oppPending } = useQuery({
    queryKey: ["opportunity-feed"],
    queryFn: () => fetchOpportunities(),
    staleTime: 30 * 1000,
    enabled: authed,
  });
  const { data: pendingHooksData } = useQuery({
    queryKey: ["pending-hooks"],
    queryFn: () => fetchPendingHooks(),
    enabled: authed,
  });
  const { data: freshLinkHooksData } = useQuery({
    queryKey: ["fresh-link-hooks"],
    queryFn: () => fetchFreshLinkHooks(),
    enabled: authed,
  });
  const fetchChanges = useServerFn(listContactChanges);
  const { data: changeData } = useQuery({
    queryKey: ["contact-changes"],
    queryFn: () => fetchChanges().catch(() => ({ changes: [] })),
    retry: false,
    enabled: authed,
  });
  const fetchRecaps = useServerFn(listPostMeetingRecaps);
  const { data: recapData } = useQuery({
    queryKey: ["post-meeting-recaps"],
    queryFn: () => fetchRecaps().catch(() => ({ recaps: [] })),
    retry: false,
    enabled: authed,
  });
  const recaps = (recapData as any)?.recaps ?? [];
  const pendingHooks = pendingHooksData?.hooks ?? [];
  const watcherHooks = freshLinkHooksData?.hooks ?? [];
  const profileChanges: ProfileChange[] = ((changeData as any)?.changes ?? []) as ProfileChange[];
  // Post-meeting prompts now render as recap cards (grouped per meeting), so
  // this deck only carries the other non-fresh-link hooks.
  const meetingHooks = pendingHooks.filter((h: any) => h.trigger_type !== "post_meeting");
  const { data: snoozedData } = useQuery({
    queryKey: ["snoozed-suggestions"],
    queryFn: () => fetchSnoozed(),
    enabled: authed,
  });
  const snoozed = snoozedData?.suggestions ?? [];
  const { data: calStatus, isPending: calStatusPending } = useQuery({
    queryKey: ["calendar-status"],
    // A 401/Response throw here (expired session, sign-out mid-render) would
    // bubble up as "[object Response]" and blank the page — treat it as
    // "not connected" instead.
    queryFn: () => fetchCalStatus().catch(() => null),
    retry: false,
    staleTime: 60_000,
    enabled: authed,
  });
  const calConnected = !!calStatus?.connected;
  // Opening the app is the moment a stale calendar hurts most, so nudge a
  // sync when the background job hasn't run in the last 30 minutes.
  const syncCalNow = useServerFn(syncGoogleCalendar);
  useEffect(() => {
    if (!authed || !calConnected) return;
    const last = calStatus?.lastSyncedAt ? new Date(calStatus.lastSyncedAt).getTime() : 0;
    if (Date.now() - last < 30 * 60 * 1000) return;
    let cancelled = false;
    syncCalNow({ data: {} })
      .then(() => {
        if (cancelled) return;
        qc.invalidateQueries({ queryKey: ["calendar-status"] });
        qc.invalidateQueries({ queryKey: ["upcoming-meetings"] });
        qc.invalidateQueries({ queryKey: ["pending-hooks"] });
        qc.invalidateQueries({ queryKey: ["post-meeting-recaps"] });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [authed, calConnected, calStatus?.lastSyncedAt]);
  const { data: googleConnections, isPending: googlePending } = useGoogleConnections();
  const gmailConnected = !!googleConnections?.google_mail?.connected;
  // Until both connection queries resolve, "not connected" is unknown — showing
  // the Live Context prompt in that window flashes it at already-connected users.
  const connectionsReady = authed && !calStatusPending && !googlePending;
  const { data: scanStatus } = useQuery({
    queryKey: ["scan-status"],
    queryFn: () => fetchScanStatus(),
    staleTime: 5 * 60 * 1000,
    enabled: authed,
  });
  const scanActive = !!scanStatus && !scanStatus.scanOverdue;

  const hookMut = useMutation({
    mutationFn: (vars: { id: string; status: "done" | "dismissed" | "snoozed"; notRelevant?: boolean }) =>
      updHook({ data: vars }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pending-hooks"] });
      qc.invalidateQueries({ queryKey: ["fresh-link-hooks"] });
      qc.invalidateQueries({ queryKey: ["home-digest"] });
    },
  });

  const rawHooks = digest?.hooks ?? [];
  const opportunities = oppData?.opportunities ?? [];
  const { hooks } = useMemo(
    () => mergeOpportunities(rawHooks, opportunities),
    [rawHooks, opportunities],
  );
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());
  // Drop hidden ids that the server has already removed.
  useEffect(() => {
    if (hiddenIds.size === 0) return;
    const present = new Set(hooks.map((h: any) => h.id));
    let changed = false;
    const next = new Set<string>();
    for (const id of hiddenIds) {
      if (present.has(id)) next.add(id);
      else changed = true;
    }
    if (changed) setHiddenIds(next);
  }, [hooks, hiddenIds]);
  const filteredHooks = useMemo(() => {
    // One cooldown rule for the whole feed: digest rows already respect it,
    // merged opportunity rows would otherwise slip past a logged touch.
    const visible = filterCooledDown(hooks.filter((h: any) => !hiddenIds.has(h.id)));
    const byPerson = new Map<string, any>();
    for (const hook of visible) {
      const normalizedName = String(hook?.contact?.name ?? "").trim().toLowerCase();
      const key = normalizedName || hook?.contact?.id || hook?.id;
      const current = byPerson.get(key);
      if (!current || (hook?.trigger_type === "fresh_signal" && current?.trigger_type !== "fresh_signal")) {
        byPerson.set(key, hook);
      }
    }
    return Array.from(byPerson.values());
  }, [hooks, hiddenIds]);

  // Travel mode: a live trip can focus the feed on the people in that city.
  const trips = useActiveTrips(authed);
  const [tripFilterId, setTripFilterId] = useState<string | null>(null);
  const activeTrip = trips.find((t) => t.id === tripFilterId) ?? null;
  useEffect(() => {
    if (tripFilterId && !trips.some((t) => t.id === tripFilterId)) setTripFilterId(null);
  }, [trips, tripFilterId]);
  const tripHooks = useMemo(() => {
    if (!activeTrip) return filteredHooks;
    const ids = new Set(activeTrip.contact_ids ?? []);
    return filteredHooks.filter((h: any) => ids.has(h?.contact?.id));
  }, [filteredHooks, activeTrip]);

  const hideHook = (id: string) =>
    setHiddenIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

  const fbMut = useMutation({
    mutationFn: (vars: {
      contactId: string;
      triggerType: string;
      action:
        | "snooze" | "snooze_1d" | "snooze_1w" | "snooze_1m" | "mute"
        | "skip" | "not_relevant" | "remember";
    }) => feedback({ data: vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["home-digest"] }),
  });
  const rememberMut = useMutation({
    mutationFn: (vars: { contactId: string; text: string }) => remember({ data: vars }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["contact", vars.contactId] });
      qc.invalidateQueries({ queryKey: ["home-digest"] });
    },
    onError: () => toast.error("Couldn't save that memory"),
  });
  const dismissMut = useMutation({
    mutationFn: (vars: { signalId: string; reason: "stale" | "irrelevant" | "uninteresting" | "remembered" | "acted" }) =>
      dismissSig({ data: vars }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["home-digest"] });
      qc.invalidateQueries({ queryKey: ["opportunity-feed"] });
    },
  });
  const snoozeSugMut = useMutation({
    mutationFn: (vars: { suggestionId: string; action: "snooze_1d" | "snooze_3d" | "snooze_1w" | "snooze_1m" | "archive" }) =>
      actSug({ data: vars }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["home-digest"] });
      qc.invalidateQueries({ queryKey: ["snoozed-suggestions"] });
    },
  });
  const wakeSugMut = useMutation({
    mutationFn: (vars: { suggestionId: string }) => wakeSug({ data: vars }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["home-digest"] });
      qc.invalidateQueries({ queryKey: ["snoozed-suggestions"] });
    },
  });
  const remMut = useMutation({
    mutationFn: (vars: { id: string; action: "complete" | "snooze_1d" | "dismiss" }) => completeRem({ data: vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reminders-due"] }),
  });
  const oppMut = useMutation({
    mutationFn: (vars: { opportunityId: string; action: "snoozed" | "dismissed" | "open"; snoozeDays?: number }) =>
      actOpp({ data: vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["opportunity-feed"] }),
  });
  const undoFb = useServerFn(undoHookFeedback);
  const undoMut = useMutation({
    mutationFn: (vars: { contactId: string; triggerType: string }) => undoFb({ data: vars }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["home-digest"] });
      qc.invalidateQueries({ queryKey: ["opportunity-feed"] });
      toast("Skip undone");
    },
  });

  // Both feeds merge into one list — render only when both have arrived,
  // otherwise the page paints a wrong count and then reshuffles.
  // The plan gates the feed, so an unloaded profile must not paint a Free page.
  const profileLoaded = !!profile && !profilePending;
  const feedReady = !digestPending && !oppPending && profileLoaded;
  // The daily target is a promise, not a cap on what we found: extras stay one tap away.
  const planDef = planOf((profile as any)?.plan);
  const dailyTarget = clampDailyTouches((profile as any)?.daily_touch_target ?? planDef.defaultDailyTouches, (profile as any)?.plan);
  // "Unknown plan" is never Free — locking is only ever shown on a loaded profile.
  const isFreePlan = profileLoaded && planDef.id === "free";
  // Hard ceiling: the expander may reveal up to the plan maximum, never past it.
  const planMax = Math.max(dailyTarget, planDef.maxDailyTouches);
  const [showAll, setShowAll] = useState(false);
  const shownCount = showAll ? planMax : dailyTarget;
  const primaryHooks = useMemo(
    () => tripHooks.slice(0, shownCount),
    [tripHooks, shownCount],
  );
  // Extras you can still open today (target below the plan max) vs. extras the plan caps off.
  const expandableCount = Math.max(0, Math.min(tripHooks.length, planMax) - shownCount);
  const cappedCount = Math.max(0, tripHooks.length - planMax);
  const lockedHooks = useMemo(
    () => tripHooks.slice(planMax, planMax + 3),
    [tripHooks, planMax],
  );
  const visibleCount = digest?.empty ? 0 : Math.min(tripHooks.length, shownCount);
  const urgentCount = digest?.empty ? 0 : countUrgent(tripHooks.slice(0, shownCount));
  const headline = !feedReady
    ? "Checking for new signals…"
    : activeTrip
      ? `${activeTrip.contact_count} in ${activeTrip.city}`
    : digest?.empty
    ? (digest.empty.reason === "no_contacts" ? "Add your first people" : "Done for today")
    : visibleCount === 0
      ? "Done for today"
      : visibleCount === 1
        ? "1 reason to reach out"
        : `${visibleCount} reasons to reach out`;
  const subheadline =
    feedReady && !digest?.empty && visibleCount > 0 && urgentCount > 0
      ? `${urgentCount} urgent`
      : undefined;

  // "Earned" calendar prompt — only after the user has actually sent a note.
  const [hasSent, setHasSent] = useState(false);
  const sentKey = user ? `orbit.sent.any:${user.id}` : null;
  useEffect(() => {
    if (!sentKey) {
      setHasSent(false);
      return;
    }
    try {
      setHasSent(localStorage.getItem(sentKey) === "1");
    } catch { /* storage unavailable */ }
  }, [sentKey]);
  const markSent = () => {
    try { if (sentKey) localStorage.setItem(sentKey, "1"); } catch { /* ignore */ }
    setHasSent(true);
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-7 pb-4">
        <div className="md:hidden">
          <PageHeader
            eyebrow={
              <>
                {scanActive && <span className="neon-pulse-dot" />}
                {new Date().toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
              </>
            }
            title={headline}
          />
        </div>

        <DesktopTodayHeader
          date={new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          headline={headline}
          subheadline={subheadline}
          scanActive={scanActive}
          connectionStatus={connectionsReady ? (
            <ConnectionStatus
              compact
              calendarConnected={calConnected}
              calendarSyncedAt={calStatus?.lastSyncedAt ?? null}
              emailConnected={gmailConnected}
            />
          ) : null}
        />

        {subheadline && (
          <p className="-mt-5 text-[14px] font-medium text-[var(--urgency-red)] md:hidden">
            {subheadline} · start at the top
          </p>
        )}

        {feedReady && trips.length > 0 && (
          <div className="space-y-2">
            {trips.map((t) => (
              <TripStrip
                key={t.id}
                trip={t}
                active={tripFilterId === t.id}
                onToggle={() => setTripFilterId((prev) => (prev === t.id ? null : t.id))}
              />
            ))}
          </div>
        )}

        <ConnectionBrokenBanner
          calendarError={calStatus?.lastSyncError ?? null}
          breakKind={(calStatus as any)?.breakKind ?? "none"}
        />


        <EnginePendingStrip enabled={authed} />

        {feedReady && (
          <CoachCard
            enabled={authed}
            calendarConnected={calConnected}
            hasFeedItem={primaryHooks.length > 0}
            topPerson={
              primaryHooks[0]?.contact?.id
                ? { id: primaryHooks[0].contact.id, name: String(primaryHooks[0].contact.name ?? "") }
                : null
            }
          />
        )}

        <WelcomeTour enabled={authed && feedReady} />

        {!feedReady && <TodayFeedSkeleton />}

        {feedReady && digest?.empty && (
          <article className="glass rounded-3xl p-8 text-center">
            <Sparkles className="mx-auto h-6 w-6 text-[var(--accent-neon-ink)]" />
            {digest.empty.reason === "no_contacts" ? (
              <>
                <p className="mt-4 text-base text-foreground/90 font-medium">
                  Add the 3 people who matter most.
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Pyng starts watching them today and writes the first message for you.
                </p>
                <button
                  type="button"
                  onClick={() => openAddDrawer("person")}
                  className="mt-6 inline-flex h-12 w-full max-w-xs items-center justify-center gap-1.5 rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground shadow-[0_10px_30px_-10px_rgba(124,168,255,0.6)]"
                  data-tour="today-add-first"
                >
                  + Add your first person
                </button>
              </>
            ) : (
              <p className="mt-3 text-sm text-foreground/90">You're all caught up — nice.</p>
            )}
          </article>
        )}

        {feedReady && !digest?.empty && activeTrip && tripHooks.length === 0 && (
          <article className="glass rounded-3xl p-6 text-center">
            <p className="text-sm text-foreground/90">
              Nothing pressing for the {activeTrip.contact_count} people in {activeTrip.city} today.
            </p>
            <Link to="/travel" className="mt-3 inline-block text-[13px] font-medium text-[var(--accent-neon-ink)]">
              Open the trip to draft "I'll be in town" notes
            </Link>
          </article>
        )}

        {feedReady && !digest?.empty && (
          <div className="pt-1" data-tour="today-focus">
          <TodayFeed
            hooks={primaryHooks}
            onSent={(h) => { markSent(); hideHook(h.id); }}
            onSnooze={(h, duration) => {
              hideHook(h.id);
              const muted = duration === "mute";
              const days = muted ? 3650 : duration === "1d" ? 1 : duration === "1m" ? 30 : 7;
              const action =
                muted ? "mute"
                : duration === "1d" ? "snooze_1d"
                : duration === "1m" ? "snooze_1m"
                : "snooze_1w";
              if (h.opportunity?.id) {
                oppMut.mutate({
                  opportunityId: h.opportunity.id,
                  action: "snoozed",
                  snoozeDays: days,
                });
              }
              if (h.suggestion_id && !muted) {
                snoozeSugMut.mutate({ suggestionId: h.suggestion_id, action: action as any });
              } else {
                fbMut.mutate({ contactId: h.contact.id, triggerType: h.trigger_type, action });
              }
              toast(
                muted ? `Muted ${h.contact.name.split(" ")[0]} — un-mute any time`
                : duration === "1d" ? "Back tomorrow"
                : duration === "1m" ? "Quiet for a month"
                : "Back next week — big news still wakes them",
              );
            }}
            onRemember={(h) => {
              hideHook(h.id);
              const text =
                (h.signal_summary || h.opportunity?.headline || h.reason_detail || h.reason_label || "").trim();
              if (text) rememberMut.mutate({ contactId: h.contact.id, text });
              fbMut.mutate({ contactId: h.contact.id, triggerType: h.trigger_type, action: "remember" });
              if (h.opportunity?.id) oppMut.mutate({ opportunityId: h.opportunity.id, action: "dismissed" });
              if (h.signal_id) {
                const raw = String(h.signal_id).replace(/^(signal|opportunity):/, "");
                if (/^[0-9a-f-]{36}$/i.test(raw)) dismissMut.mutate({ signalId: raw, reason: "remembered" });
              }
              toast.success(`Saved to ${h.contact.name.split(" ")[0]}'s memory`);
            }}
            onNotRelevant={(h, reason) => {
              hideHook(h.id);
              if (h.opportunity?.id) {
                oppMut.mutate({ opportunityId: h.opportunity.id, action: "dismissed" });
              }
              if (h.signal_id) {
                const raw = String(h.signal_id).replace(/^(signal|opportunity):/, "");
                if (/^[0-9a-f-]{36}$/i.test(raw)) dismissMut.mutate({ signalId: raw, reason });
              }
              fbMut.mutate({ contactId: h.contact.id, triggerType: h.trigger_type, action: "not_relevant" });
              toast(
                reason === "stale" ? "Got it — too old"
                : reason === "irrelevant" ? "Got it — not about them"
                : "Got it — fewer like this",
                {
                  action: {
                    label: "Undo",
                    onClick: () => {
                      setHiddenIds((prev) => {
                        const next = new Set(prev);
                        next.delete(h.id);
                        return next;
                      });
                      undoMut.mutate({ contactId: h.contact.id, triggerType: h.trigger_type });
                      if (h.opportunity?.id) {
                        oppMut.mutate({ opportunityId: h.opportunity.id, action: "open" });
                      }
                    },
                  },
                },
              );
            }}
            onSkip={(h) => {
              hideHook(h.id);
              if (h.opportunity?.id) {
                oppMut.mutate({ opportunityId: h.opportunity.id, action: "dismissed" });
              }
              fbMut.mutate({ contactId: h.contact.id, triggerType: h.trigger_type, action: "skip" });
              toast("Skipped for today", {
                action: {
                  label: "Undo",
                  onClick: () => {
                    setHiddenIds((prev) => {
                      const next = new Set(prev);
                      next.delete(h.id);
                      return next;
                    });
                    undoMut.mutate({ contactId: h.contact.id, triggerType: h.trigger_type });
                    if (h.opportunity?.id) {
                      oppMut.mutate({ opportunityId: h.opportunity.id, action: "open" });
                    }
                  },
                },
              });
            }}
            onArchive={(h) => {
              if (!h.suggestion_id) return;
              hideHook(h.id);
              snoozeSugMut.mutate({ suggestionId: h.suggestion_id, action: "archive" });
              toast("Archived");
            }}
          />
          {(expandableCount > 0 || showAll) && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-3 w-full rounded-2xl glass-soft px-4 py-3 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {showAll
                ? "Show today's focus only"
                : expandableCount === 1
                  ? "1 more waiting"
                  : `${expandableCount} more waiting`}
            </button>
          )}
          {isFreePlan
            ? cappedCount > 0 && <LockedMoments hooks={lockedHooks} total={cappedCount} />
            : (showAll || expandableCount === 0) && (
                <p className="mt-3 px-4 text-center text-sm text-muted-foreground">
                  {cappedCount > 0
                    ? "You're caught up for today — more tomorrow."
                    : "That's everything worth a Pyng today."}
                </p>
              )}
          </div>
        )}

        {connectionsReady && !digest?.empty && hasSent && !(calConnected && gmailConnected) && (
          <CalendarConnectCard
            locked={isFreePlan}
            calendarConnected={calConnected}
            emailConnected={gmailConnected}
            copy="Nice — you sent one. Turn on Live Context and Pyng preps you before every meeting, then follows up after."
          />
        )}

        {feedReady && !digest?.empty && (
          <div className="space-y-7" data-tour="today-more">
              {(digest?.counts?.people ?? 0) >= 3 && <PushOptInRow />}

              {connectionsReady && !(calConnected && gmailConnected) && !hasSent && (
                <CalendarConnectCard
                  soft
                  locked={isFreePlan}
                  calendarConnected={calConnected}
                  emailConnected={gmailConnected}
                  copy="Turn on Live Context — Pyng reads your calendar and inbox so every reach-out knows what just happened."
                />
              )}

              {(reminders ?? []).length > 0 && (
                <section className="space-y-3">
                  <h2 className="text-[15px] font-semibold text-foreground/90 flex items-center gap-2">
                    <Bell className="h-4 w-4 text-primary" /> Reminders due
                  </h2>
                  {(reminders ?? []).map((r: any) => (
                    <article key={r.id} className="glass rounded-3xl p-4 flex items-center gap-3">
                      <Avatar name={r.contact?.name ?? ""} initials={r.contact?.initials} photoUrl={r.contact?.photo_url} size={44} />
                      <div className="min-w-0 flex-1">
                        <p className="text-[15px] leading-snug">
                          <span className="font-medium">{r.contact?.name}</span>{" "}
                          <span className="text-muted-foreground">— {r.title}</span>
                        </p>
                        <p className="mt-0.5 text-[13px] text-muted-foreground">Due {new Date(r.due_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => remMut.mutate({ id: r.id, action: "complete" })} title="Done"
                          className="grid h-10 w-10 place-items-center rounded-full glass-soft hover:bg-primary hover:text-primary-foreground">
                          <Check className="h-4 w-4" />
                        </button>
                        <button onClick={() => remMut.mutate({ id: r.id, action: "snooze_1d" })} title="Tomorrow"
                          className="grid h-10 w-10 place-items-center rounded-full glass-soft">
                          <Clock className="h-4 w-4" />
                        </button>
                        <button onClick={() => remMut.mutate({ id: r.id, action: "dismiss" })} title="Dismiss"
                          className="grid h-10 w-10 place-items-center rounded-full glass-soft">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </article>
                  ))}
                </section>
              )}

              {(watcherHooks.length > 0 || profileChanges.length > 0) && (
                <section className="space-y-3">
                  <h2 className="text-[15px] font-semibold text-foreground/90 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" /> Worth remembering
                  </h2>
                  <p className="text-[13px] leading-snug text-muted-foreground/80">
                    Quiet finds from this month. Keep the useful ones, mute the noise.
                  </p>
                  <HookDeck>
                    {[
                    ...profileChanges.map((c) => (
                      <ProfileChangeCard key={`change:${c.id}`} change={c} />
                    )),
                    ...watcherHooks.map((h: any) => (
                    <PostMeetingHook
                      key={h.id}
                      hook={h}
                      onDone={() => hookMut.mutate({ id: h.id, status: "done" })}
                      onSkip={() => {
                        hookMut.mutate({ id: h.id, status: "dismissed", notRelevant: true });
                        const signalId = String(h.id).startsWith("signal:")
                          ? String(h.id).slice("signal:".length)
                          : null;
                        toast(
                          "Got it — less of these",
                          signalId
                            ? {
                                action: {
                                  label: "Undo",
                                  onClick: () => {
                                    undoSignal({ data: { signalId } }).then(() => {
                                      qc.invalidateQueries({ queryKey: ["fresh-link-hooks"] });
                                      qc.invalidateQueries({ queryKey: ["home-digest"] });
                                    });
                                  },
                                },
                              }
                            : undefined,
                        );
                      }}
                    />
                    )),
                    ]}
                  </HookDeck>
                </section>
              )}

              {recaps.length > 0 && (
                <section className="space-y-3">
                  <h2 className="text-[15px] font-semibold text-foreground/90 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" /> Just met — capture it
                  </h2>
                  <HookDeck>
                    {recaps.map((r: any) => (
                      <PostMeetingRecapCard key={r.eventId} recap={r} />
                    ))}
                  </HookDeck>
                </section>
              )}

              {meetingHooks.length > 0 && (
                <section className="space-y-3">
                  <h2 className="text-[15px] font-semibold text-foreground/90 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" /> Nudges
                  </h2>
                  <HookDeck>
                    {meetingHooks.map((h: any) => (
                    <PostMeetingHook
                      key={h.id}
                      hook={h}
                      onDone={() => hookMut.mutate({ id: h.id, status: "done" })}
                      onSkip={() => hookMut.mutate({ id: h.id, status: "dismissed", notRelevant: true })}
                    />
                    ))}
                  </HookDeck>
                </section>
              )}

              <ComingUpCard />


              {snoozed.length > 0 && (
                <details className="group rounded-3xl glass-soft p-4 transition-all">
                  <summary className="flex cursor-pointer list-none items-center justify-between text-[15px] font-semibold text-foreground/90">
                    <span className="inline-flex items-center gap-2">
                      <Inbox className="h-4 w-4 text-primary" /> Snoozed
                      <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[12px] text-primary">
                        {snoozed.length}
                      </span>
                    </span>
                    <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="mt-3">
                    <SnoozedList
                      items={snoozed}
                      onWake={(id) => {
                        wakeSugMut.mutate({ suggestionId: id });
                        toast.success("Back on Today");
                      }}
                      onArchive={(id) => {
                        snoozeSugMut.mutate({ suggestionId: id, action: "archive" });
                        toast("Archived");
                      }}
                    />
                  </div>
                </details>
              )}

              {scanStatus && (
                <p className="pt-1 text-[13px] leading-relaxed text-muted-foreground/80 text-center">
                  {scanStatus.lastRunAt
                    ? scanStatus.scanOverdue
                      ? `Pyng last scanned ${formatRelativeShort(scanStatus.lastRunAt)} — scan overdue${scanStatus.linksDisabled > 0 ? ` · ${scanStatus.linksDisabled} link${scanStatus.linksDisabled === 1 ? "" : "s"} paused` : ""}`
                      : `Pyng last scanned ${formatRelativeShort(scanStatus.lastRunAt)} · ${scanStatus.newLast24h} new signal${scanStatus.newLast24h === 1 ? "" : "s"} today · ${scanStatus.newLast7d} this week${scanStatus.linksDisabled > 0 ? ` · ${scanStatus.linksDisabled} link${scanStatus.linksDisabled === 1 ? "" : "s"} paused` : ""}`
                    : scanStatus.linksTracked === 0
                      ? "Add a LinkedIn or website link to a person so Pyng can watch for updates."
                      : "Pyng hasn't scanned yet — first scan runs within 24 hours."}
                </p>
              )}

          </div>
        )}
        {digest && (
          digest.empty?.reason === "no_contacts" ? (
            <RailsTutorial
              storageKey="orbit.tour.today.empty.v1"
              steps={TODAY_EMPTY_STEPS}
            />
          ) : !digest.empty ? (
            <RailsTutorial
              storageKey="orbit.tour.today.v1"
              steps={TODAY_STEPS}
            />
          ) : null
        )}

        {/* Beta escape hatch: one tap to tell us what broke. */}
        <div className="pb-[calc(env(safe-area-inset-bottom)+1.5rem)] pt-1 text-center">
          <button
            type="button"
            onClick={() => setFeedbackOpen(true)}
            className="text-[12px] text-muted-foreground/70 underline-offset-4 hover:underline"
          >
            Something off? Send feedback
          </button>
        </div>
        <FeedbackSheet open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      </div>
    </TooltipProvider>
  );
}

const TODAY_EMPTY_STEPS: RailsStep[] = [
  {
    eyebrow: "Welcome",
    title: "This is your daily 30 seconds.",
    body: "Pyng shows up here with one card per person worth reaching out to today.",
  },
  {
    eyebrow: "Start small",
    title: "Add the 3 people who matter most.",
    body: "An investor, a mentor, a key client — Pyng pulls their context automatically.",
    target: "[data-tour='today-add-first']",
    durationMs: 4000,
  },
  {
    eyebrow: "Then come back",
    title: "We'll ping you when something moves.",
    body: "A new role, a launch, a trip — a drafted message will be ready to send.",
  },
];

const TODAY_STEPS: RailsStep[] = [
  {
    eyebrow: "Today's feed",
    title: "Who to reach out to, ranked.",
    body: "Red means act now. Green is a live opening. Blue is worth a congrats. Amber means you owe them.",
    target: "[data-tour='today-focus']",
  },
  {
    eyebrow: "Tap a row",
    title: "The note is already written.",
    body: "Read the reason, edit if you want, then send. We copy it and open Messages or Mail — Pyng never sends silently.",
    target: "[data-tour='today-focus']",
  },
  {
    eyebrow: "Teach Pyng",
    title: "Make Pyng smarter.",
    body: "Quiet finds show up below the feed. Keep the useful ones as memory, mute the noise — the feed gets sharper every day.",
    target: "[data-tour='today-more']",
    durationMs: 4000,
  },
];

function PostMeetingHook({
  hook,
  onDone,
  onSkip,
}: {
  hook: any;
  onDone: () => void;
  onSkip: () => void;
}) {
  const [open, setOpen] = useState(false);
  const saveMemory = useServerFn(saveHookMemory);
  const qcHook = useQueryClient();
  const quickSave = useMutation({
    mutationFn: async () =>
      saveMemory({
        data: {
          hookId: hook.id,
          contactId: hook.contact_id,
          calendarEventId: hook.calendar_event_id ?? undefined,
        },
      }),
    onSuccess: () => {
      qcHook.invalidateQueries({ queryKey: ["home-digest"] });
      qcHook.invalidateQueries({ queryKey: ["pending-hooks"] });
      qcHook.invalidateQueries({ queryKey: ["fresh-link-hooks"] });
      qcHook.invalidateQueries({ queryKey: ["contact", hook.contact_id] });
      toast.success("Saved to memory");
      setOpen(false);
      onDone();
    },
    onError: (e: any) => toast.error(e?.message ?? "Couldn't save"),
  });
  const person = hook.contact ?? null;
  const { open: openPerson } = useContactSheet();
  const when = hook.scheduled_for
    ? new Date(hook.scheduled_for).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : null;
  const roleLine = [person?.title, person?.company].filter(Boolean).join(" · ");
  return (
    <article className="glass rounded-3xl p-4 ring-1 ring-primary/10">
      {(() => {
        const head = (
          <>
            {person ? (
              <Avatar name={person.name ?? ""} initials={person.initials} photoUrl={person.photo_url} size={44} />
            ) : (
              <div className="grid h-11 w-11 place-items-center rounded-full bg-primary/15 text-primary shrink-0">
                <Sparkles className="h-5 w-5" />
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate text-[16px] font-medium">{person?.name ?? hook.title}</p>
              <p className="mt-0.5 truncate text-[13px] text-muted-foreground">
                {roleLine || hook.title}
                {when ? ` · ${when}` : ""}
              </p>
            </div>
          </>
        );
        return hook.contact_id ? (
          <button
            type="button"
            onClick={() => openPerson(hook.contact_id)}
            className="grid w-full grid-cols-[auto_minmax(0,1fr)] items-center gap-3 text-left rounded-2xl -m-1 p-1 transition-colors active:bg-foreground/5"
          >
            {head}
          </button>
        ) : (
          <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3">{head}</div>
        );
      })()}

      {hook.body && (
        <p className="mt-3 text-[15px] leading-relaxed text-foreground/85">{hook.body}</p>
      )}
      {hook.context?.event_title && (
        <p className="mt-1.5 text-[13px] text-muted-foreground">{hook.context.event_title}</p>
      )}

      <div className="mt-4 grid grid-cols-2 gap-2.5">
        <button
          onClick={() => hook.contact_id && quickSave.mutate()}
          disabled={quickSave.isPending || !hook.contact_id}
          className="inline-flex h-11 items-center justify-center rounded-full px-4 text-[15px] font-medium bg-primary text-primary-foreground transition-colors disabled:opacity-60"
        >
          {quickSave.isPending ? "Saving…" : "Remember"}
        </button>
        <button
          onClick={onSkip}
          className="inline-flex h-11 items-center justify-center gap-1.5 rounded-full glass-soft px-4 text-[15px] text-foreground/80"
        >
          <X className="h-4 w-4" /> Not relevant
        </button>
      </div>
      {hook.contact_id && !open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-2 text-[12px] text-muted-foreground hover:text-foreground"
        >
          + Add a note
        </button>
      )}
      {open && hook.contact_id && (
        <InlineMemoryComposer
          hookId={hook.id}
          contactId={hook.contact_id}
          calendarEventId={hook.calendar_event_id ?? undefined}
          onSuccess={() => {
            setOpen(false);
            onDone();
          }}
        />

      )}
    </article>
  );
}



function SnoozedList({
  items,
  onWake,
  onArchive,
}: {
  items: any[];
  onWake: (suggestionId: string) => void;
  onArchive: (suggestionId: string) => void;
}) {
  if (items.length === 0) {
    return (
      <p className="text-center text-[14px] text-muted-foreground py-6">
        Nothing snoozed.
      </p>
    );
  }
  const fmtUntil = (iso: string | null) => {
    if (!iso) return "Snoozed";
    const d = new Date(iso);
    const days = Math.max(0, Math.round((d.getTime() - Date.now()) / 86400000));
    if (days === 0) return "Wakes today";
    if (days === 1) return "Wakes tomorrow";
    return `Wakes in ${days}d`;
  };
  return (
    <div className="space-y-3">
      {items.map((s) => (
        <article key={s.id} className="glass rounded-3xl p-4 flex items-center gap-3">
          <Avatar
            name={s.contact?.name ?? ""}
            initials={s.contact?.initials}
            photoUrl={s.contact?.photo_url}
            size={44}
          />
          <div className="min-w-0 flex-1">
            <p className="text-[15px] truncate">
              <span className="font-medium">{s.contact?.name}</span>
              {s.contact?.is_vip && (
                <Star className="ml-1 inline h-3.5 w-3.5 fill-primary text-primary" />
              )}
            </p>
            <p className="mt-0.5 text-[13px] text-muted-foreground truncate">
              {fmtUntil(s.snooze_until)} · {s.reason ?? "Outreach"}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => onWake(s.id)}
              title="Wake now"
              className="grid h-10 w-10 place-items-center rounded-full glass-soft hover:bg-primary hover:text-primary-foreground"
            >
              <Undo2 className="h-4 w-4" />
            </button>
            <button
              onClick={() => onArchive(s.id)}
              title="Archive"
              className="grid h-10 w-10 place-items-center rounded-full glass-soft"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}
